import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@rudi/ui/button';
import { ChatModelFields } from '../components/ChatModelFields';
import { OpenRouterModelSearch, searchModelOptions } from '../components/OpenRouterModelSearch';
import { PageHeader } from '../components/page';
import {
  chatDefaultsError,
  platformExecutors,
  usePlatformChatDefaults,
  useRefreshOpenRouterModels,
  useUpdatePlatformChatDefaults,
  type ChatExecutor,
  type ModelDefault,
  type PlatformChatDefaults,
} from '../api/chat-defaults';

/** The CLI executors, whose models all belong to one provider. */
const CLI_EXECUTORS: ChatExecutor[] = ['claude_code', 'codex'];

// The preferred platform default: Claude Code on Opus 5.5 with high reasoning.
const PRESET_BACKEND: ChatExecutor = 'claude_code';
const PRESET_MODEL = 'claude-opus-5-5';
const PRESET_EFFORT = 'high';
const PRESET_LABEL = 'Claude Code CLI · Claude Opus 5.5 · High';

/** Who serves an API model, from its id's prefix. */
type ApiProvider = 'openrouter' | 'openai' | 'deepinfra' | 'infercom';
const API_PROVIDERS: Record<ApiProvider, string> = {
  openrouter: 'OpenRouter',
  openai: 'OpenAI',
  deepinfra: 'DeepInfra',
  infercom: 'Infercom',
};
const RETIRED_NOTE = 'no longer offered';

function providerOf(id: string): ApiProvider {
  const prefix = id.split('/')[0];
  return prefix === 'deepinfra' || prefix === 'infercom' || prefix === 'openrouter'
    ? prefix
    : 'openai';
}

function firstEffort(efforts: string[]): string {
  return efforts.includes('medium') ? 'medium' : efforts[0];
}

export function PlatformChatDefaultsForm({ defaults }: { defaults: PlatformChatDefaults }) {
  const mutation = useUpdatePlatformChatDefaults();
  const refresh = useRefreshOpenRouterModels();
  const [draft, setDraft] = useState<PlatformChatDefaults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [defaultQuery, setDefaultQuery] = useState('');
  const [fallbackQuery, setFallbackQuery] = useState('');
  const current = draft ?? defaults;
  const executors = platformExecutors(defaults);
  const modelOf = (backend: ChatExecutor) => current.models[backend] as ModelDefault;
  const model = modelOf(current.ai_chat_backend);
  const provider = CLI_EXECUTORS.includes(current.ai_chat_backend)
    ? current.ai_chat_backend
    : providerOf(model.model);
  const options = (defaults.model_options[current.ai_chat_backend] ?? []).filter(
    item => CLI_EXECUTORS.includes(provider as ChatExecutor) || providerOf(item.model) === provider
  );
  const apiOptionsOf = (id: ApiProvider) =>
    defaults.model_options.api.filter(item => providerOf(item.model) === id);
  const fallback = current.fallback;
  const fallbackProvider = fallback ? providerOf(fallback.model) : 'openrouter';
  const fallbackOptions = apiOptionsOf(fallbackProvider);
  const openRouterCount = apiOptionsOf('openrouter').length;
  const fallbackValid =
    !fallback ||
    (fallback.model === defaults.fallback?.model &&
      fallback.effort === defaults.fallback?.effort) ||
    defaults.model_options.api.some(
      option => option.model === fallback.model && option.efforts.includes(fallback.effort)
    );
  const changed =
    !!draft &&
    (draft.ai_chat_backend !== defaults.ai_chat_backend ||
      draft.codex_fallback !== defaults.codex_fallback ||
      draft.fallback?.model !== defaults.fallback?.model ||
      draft.fallback?.effort !== defaults.fallback?.effort ||
      executors.some(
        backend =>
          draft.models[backend]?.model !== defaults.models[backend]?.model ||
          draft.models[backend]?.effort !== defaults.models[backend]?.effort
      ));
  const conflict = !!draft && draft.revision !== defaults.revision;
  const valid =
    fallbackValid &&
    executors.every(backend => {
      const choice = modelOf(backend);
      // Preserve inactive legacy defaults unless this edit changes them.
      if (
        backend !== current.ai_chat_backend &&
        choice.model === defaults.models[backend]?.model &&
        choice.effort === defaults.models[backend]?.effort
      )
        return true;
      return (defaults.model_options[backend] ?? []).some(
        option => option.model === choice.model && option.efforts.includes(choice.effort)
      );
    });
  const presetAvailable = (defaults.model_options[PRESET_BACKEND] ?? []).some(
    option => option.model === PRESET_MODEL && option.efforts.includes(PRESET_EFFORT)
  );
  const loadModels = () => {
    refresh.reset();
    refresh.mutate();
  };
  const refreshError = refresh.error ? chatDefaultsError(refresh.error) : null;
  const update = (next: PlatformChatDefaults) => {
    setDraft(next);
    setError(null);
    setSaved(false);
  };
  const save = async () => {
    if (!changed || !valid || conflict || mutation.isPending) return;
    setError(null);
    setSaved(false);
    try {
      await mutation.mutateAsync({
        schema_version: 1,
        expected_revision: current.revision,
        ai_chat_backend: current.ai_chat_backend,
        models: current.models,
        ...(current.codex_fallback === undefined ? {} : { codex_fallback: current.codex_fallback }),
        ...(current.fallback === undefined ? {} : { fallback: current.fallback }),
      });
      setDraft(null);
      setSaved(true);
    } catch (error) {
      setError(chatDefaultsError(error));
    }
  };

  return (
    <form
      aria-label="Platform chat defaults"
      className="max-w-xl space-y-4 rounded-3xl border p-4"
      onSubmit={event => {
        event.preventDefault();
        void save();
      }}
    >
      <div>
        <h2 className="text-sm font-medium">Platform default</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Applies to new chats in tenants that follow the platform default. Tenant overrides and
          existing chats keep their settings.
        </p>
      </div>
      <label className="block space-y-1 text-sm">
        <span>Provider</span>
        <select
          aria-label="Platform chat provider"
          value={provider}
          disabled={mutation.isPending}
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          onChange={event => {
            const next = event.target.value;
            const backend: ChatExecutor = CLI_EXECUTORS.includes(next as ChatExecutor)
              ? (next as ChatExecutor)
              : 'api';
            const cli = backend !== 'api';
            const choice = (defaults.model_options[backend] ?? []).find(
              item => cli || providerOf(item.model) === next
            );
            if (!choice) return;
            const kept = current.models[backend];
            update({
              ...current,
              ai_chat_backend: backend,
              models: {
                ...current.models,
                [backend]:
                  kept && (cli || providerOf(kept.model) === next)
                    ? kept
                    : { model: choice.model, effort: firstEffort(choice.efforts) },
              },
            });
          }}
        >
          {Object.entries({
            claude_code: 'Claude Code CLI',
            codex: 'Codex CLI',
            deepinfra: 'DeepInfra',
            infercom: 'Infercom',
            openrouter: 'OpenRouter',
            openai: 'OpenAI',
          }).map(([id, name]) => {
            const available = CLI_EXECUTORS.includes(id as ChatExecutor)
              ? executors.includes(id as ChatExecutor)
              : defaults.model_options.api.some(item => providerOf(item.model) === id);
            return (
              <option key={id} value={id} disabled={!available}>
                {name}
                {!available ? ' · unavailable' : ''}
              </option>
            );
          })}
        </select>
      </label>
      {provider === 'openrouter' && (
        <OpenRouterModelSearch
          label="Chat"
          query={defaultQuery}
          onQuery={setDefaultQuery}
          count={openRouterCount}
          fetchedAt={defaults.openrouter_models_fetched_at}
          onRefresh={loadModels}
          refreshing={refresh.isPending}
          error={refreshError}
          disabled={mutation.isPending}
        />
      )}
      <ChatModelFields
        model={model.model}
        effort={model.effort}
        options={
          provider === 'openrouter'
            ? searchModelOptions(options, defaultQuery, model.model)
            : options
        }
        unlistedNote={provider === 'openrouter' ? RETIRED_NOTE : undefined}
        disabled={mutation.isPending}
        onChange={(model, effort) =>
          update({
            ...current,
            models: { ...current.models, [current.ai_chat_backend]: { model, effort } },
          })
        }
      />
      {current.ai_chat_backend === 'api' && (
        <p className="text-sm text-muted-foreground">
          OpenAI models use each tenant's OpenAI API key, which must have access to the selected
          model. DeepInfra, Infercom and OpenRouter use the application's deployment key from
          OpenBao, unless a tenant or Cortex global key overrides it.
        </p>
      )}
      {(current.codex_fallback !== undefined || fallback !== undefined) && (
        <section aria-label="Fallback" className="space-y-3 rounded-3xl border p-3">
          <h3 className="text-sm font-medium">Fallback</h3>
          {current.codex_fallback !== undefined && (
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={current.codex_fallback}
                disabled={mutation.isPending}
                onChange={event => update({ ...current, codex_fallback: event.target.checked })}
              />
              <span>
                <span className="block">Fall back to another model</span>
                <span className="block text-muted-foreground">
                  {fallback
                    ? 'When a Claude Code or Codex chat turn fails before it answers, or its runner is not ready, the turn runs on the model below, and the chat stays there. Needs an API key for that model. When OpenRouter no longer offers it, the API model the team, tenant or platform sets answers instead.'
                    : 'When a Claude Code or Codex chat turn fails before it answers, or its runner is not ready, the turn runs on the API model the team, tenant or platform sets, and the chat stays there. Needs an API key for that model.'}
                </span>
              </span>
            </label>
          )}
          {fallback && (
            <>
              <label className="block space-y-1 text-sm">
                <span>Provider</span>
                <select
                  aria-label="Fallback provider"
                  value={fallbackProvider}
                  disabled={mutation.isPending}
                  className="h-9 w-full rounded-3xl border border-input bg-background px-3"
                  onChange={event => {
                    const choice = apiOptionsOf(event.target.value as ApiProvider)[0];
                    if (!choice) return;
                    update({
                      ...current,
                      fallback: { model: choice.model, effort: firstEffort(choice.efforts) },
                    });
                  }}
                >
                  {(Object.keys(API_PROVIDERS) as ApiProvider[]).map(id => {
                    const available = apiOptionsOf(id).length > 0;
                    return (
                      <option key={id} value={id} disabled={!available}>
                        {API_PROVIDERS[id]}
                        {!available ? ' · unavailable' : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
              {fallbackProvider === 'openrouter' && (
                <OpenRouterModelSearch
                  label="Fallback"
                  query={fallbackQuery}
                  onQuery={setFallbackQuery}
                  count={openRouterCount}
                  fetchedAt={defaults.openrouter_models_fetched_at}
                  onRefresh={loadModels}
                  refreshing={refresh.isPending}
                  error={refreshError}
                  disabled={mutation.isPending}
                />
              )}
              <ChatModelFields
                label="Fallback"
                model={fallback.model}
                effort={fallback.effort}
                options={
                  fallbackProvider === 'openrouter'
                    ? searchModelOptions(fallbackOptions, fallbackQuery, fallback.model)
                    : fallbackOptions
                }
                unlistedNote={fallbackProvider === 'openrouter' ? RETIRED_NOTE : undefined}
                disabled={mutation.isPending || current.codex_fallback === false}
                onChange={(model, effort) => update({ ...current, fallback: { model, effort } })}
              />
            </>
          )}
        </section>
      )}
      <div className="space-y-2 rounded-3xl bg-muted p-3">
        <p className="text-sm">Preferred default: {PRESET_LABEL}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={mutation.isPending || !presetAvailable}
          onClick={() =>
            update({
              ...current,
              ai_chat_backend: PRESET_BACKEND,
              models: {
                ...current.models,
                [PRESET_BACKEND]: { model: PRESET_MODEL, effort: PRESET_EFFORT },
              },
            })
          }
        >
          Use preferred default
        </Button>
        {!presetAvailable && (
          <p className="text-xs text-muted-foreground">
            This Rudi connection does not offer Claude Code with Claude Opus 5.5 on high reasoning
            yet.
          </p>
        )}
      </div>
      {conflict && (
        <p role="alert" className="text-sm text-destructive">
          The platform defaults changed while you were editing. Discard your edits to load the
          latest defaults.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm">
          Platform chat defaults saved.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={mutation.isPending || !changed || !valid || conflict}>
          {mutation.isPending ? 'Saving…' : 'Save platform default'}
        </Button>
        {draft && (
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => {
              setDraft(null);
              setError(null);
              setSaved(false);
            }}
          >
            Discard edits
          </Button>
        )}
      </div>
    </form>
  );
}

export function ModelsPage() {
  const defaults = usePlatformChatDefaults();
  return (
    <div className="space-y-4">
      <PageHeader
        title="Rudi language models"
        description="Manage chat defaults for the connected Rudi platform."
      />
      {defaults.error && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{chatDefaultsError(defaults.error)}</p>
          <Button variant="outline" onClick={() => void defaults.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {defaults.data ? (
        <PlatformChatDefaultsForm defaults={defaults.data} />
      ) : defaults.isLoading ? (
        <p>Loading chat defaults…</p>
      ) : defaults.error ? null : (
        <section className="space-y-2 rounded-3xl border p-4">
          <h2 className="text-sm font-medium">Platform defaults unavailable</h2>
          <p className="text-sm text-muted-foreground">
            This Rudi connection does not support editing platform chat defaults. Rudi needs to
            support this setting before you can change it here.
          </p>
          <p className="text-sm">Preferred default: {PRESET_LABEL}</p>
          <Button variant="outline" onClick={() => void defaults.refetch()}>
            Check again
          </Button>
        </section>
      )}
      <p className="text-sm text-muted-foreground">
        Set tenant overrides under{' '}
        <Link to="/tenants" className="underline">
          Tenants
        </Link>{' '}
        → Chat defaults.
      </p>
    </div>
  );
}
